import { useState } from "react";

// ─── Demo credentials for each role ───────────────────────────────────────────
const DEMO_ACCOUNTS = [
  {
    role:     "Student",
    icon:     "🎓",
    email:    "demo.student@ojthub.ph",
    password: "DemoStudent2026!",
    color:    "#0a6e4e",
    desc:     "Browse jobs, track OJT hours, logbook & certificates",
  },
  {
    role:     "Employer",
    icon:     "🏢",
    email:    "demo.employer@ojthub.ph",
    password: "DemoEmployer2026!",
    color:    "#00283c",
    desc:     "Post jobs, review applications, manage interns",
  },
  {
    role:     "University",
    icon:     "🏫",
    email:    "demo.university@ojthub.ph",
    password: "DemoUniversity2026!",
    color:    "#7c3aed",
    desc:     "Track all students, approve logbooks, generate reports",
  },
];

// ─── Inline banner shown when logged in as a demo account ─────────────────────
export function DemoModeBanner({ role = "student", userEmail }) {
  const [signingOut, setSigningOut] = useState(false);

  const icons  = { student:"🎓", employer:"🏢", coordinator:"🏫", university_admin:"🏫", department_head:"🏫" };
  const labels = { student:"Student", employer:"Employer", coordinator:"University Coordinator", university_admin:"University Admin", department_head:"Dept Head" };

  const DEMO_EMAILS = [
    "demo.student@ojthub.ph",
    "demo.employer@ojthub.ph",
    "demo.university@ojthub.ph",
  ];

  // Only show for demo accounts
  if (!DEMO_EMAILS.includes(userEmail)) return null;

  const signOut = async () => {
    setSigningOut(true);
    const { supabase } = await import("./supabase");
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: "linear-gradient(90deg, #f5a623 0%, #d4891a 100%)",
      padding: "7px 16px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      boxShadow: "0 2px 12px rgba(245,166,35,.4)",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0 }}>
        <span style={{ fontSize:15, flexShrink:0 }}>{icons[role] || "👁️"}</span>
        <div style={{ minWidth:0 }}>
          <span style={{ fontWeight:800, fontSize:12, color:"#00283c" }}>
            DEMO · {labels[role] || role}
          </span>
          <span style={{ fontSize:11, color:"rgba(0,40,60,.6)", marginLeft:8 }}>
            {userEmail}
          </span>
        </div>
      </div>

      <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
        <a href="/" style={{
          background:"#00283c", color:"#fff",
          padding:"4px 14px", borderRadius:100,
          fontSize:11, fontWeight:700, textDecoration:"none",
          whiteSpace:"nowrap",
        }}>
          Create Free Account →
        </a>
        <button
          onClick={signOut}
          disabled={signingOut}
          style={{
            background:"rgba(0,40,60,.15)", border:"1.5px solid rgba(0,40,60,.3)",
            color:"#00283c", padding:"4px 14px", borderRadius:100,
            fontSize:11, fontWeight:800, cursor: signingOut ? "not-allowed" : "pointer",
            whiteSpace:"nowrap", fontFamily:"inherit",
          }}
        >
          {signingOut ? "Signing out…" : "Sign Out"}
        </button>
      </div>
    </div>
  );
}

// ─── Demo picker modal — shown on landing page "Try Demo" CTA ─────────────────
export default function DemoLoginPicker({ onClose, onPickAccount }) {
  const [copied, setCopied] = useState(null);
  const [selected, setSelected] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:2000,
        background:"rgba(0,0,0,.55)", backdropFilter:"blur(8px)",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background:"#fff", borderRadius:24, padding:"40px 36px",
          maxWidth:520, width:"100%",
          boxShadow:"0 32px 80px rgba(0,0,0,.22)",
          fontFamily:"'DM Sans', sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <img src="/OJT_logo.png" alt="OJT Hub" style={{ height:44, marginBottom:14, objectFit:"contain" }} />
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:900,
            color:"#0b1f18", margin:0, marginBottom:6 }}>
            Try OJT Hub — Free Demo
          </h2>
          <p style={{ fontSize:13, color:"#4a6b5c", margin:0 }}>
            Pick a role to explore the full platform. No signup needed.
          </p>
        </div>

        {/* Role cards */}
        <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:28 }}>
          {DEMO_ACCOUNTS.map(acc => (
            <div
              key={acc.role}
              onClick={() => setSelected(selected === acc.role ? null : acc.role)}
              style={{
                border:`2px solid ${selected === acc.role ? acc.color : "rgba(0,0,0,.1)"}`,
                borderRadius:14, padding:"14px 18px", cursor:"pointer",
                background: selected === acc.role ? `${acc.color}09` : "#fafafa",
                transition:"all .2s",
              }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: selected === acc.role ? 12 : 0 }}>
                <span style={{ fontSize:22 }}>{acc.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:800, fontSize:14, color:"#0b1f18" }}>
                    {acc.role} Demo
                  </div>
                  <div style={{ fontSize:12, color:"#4a6b5c" }}>{acc.desc}</div>
                </div>
                <div style={{
                  width:20, height:20, borderRadius:"50%",
                  border:`2px solid ${selected === acc.role ? acc.color : "#ccc"}`,
                  background: selected === acc.role ? acc.color : "transparent",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  flexShrink:0,
                }}>
                  {selected === acc.role && <span style={{ color:"#fff", fontSize:11, fontWeight:900 }}>✓</span>}
                </div>
              </div>

              {/* Expanded credentials */}
              {selected === acc.role && (
                <div style={{ borderTop:"1px solid rgba(0,0,0,.08)", paddingTop:12, display:"flex", flexDirection:"column", gap:8 }}>
                  {[
                    { label:"Email", value:acc.email, key:`${acc.role}-email` },
                    { label:"Password", value:acc.password, key:`${acc.role}-pw` },
                  ].map(({ label, value, key }) => (
                    <div key={key} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:acc.color,
                        textTransform:"uppercase", letterSpacing:".04em", width:64, flexShrink:0 }}>
                        {label}
                      </span>
                      <code style={{ fontSize:12, background:"rgba(0,0,0,.06)",
                        padding:"3px 10px", borderRadius:6, flex:1, color:"#0b1f18" }}>
                        {value}
                      </code>
                      <button
                        onClick={e => { e.stopPropagation(); copy(value, key); }}
                        style={{ fontSize:11, fontWeight:700, color:acc.color, background:"none",
                          border:`1.5px solid ${acc.color}`, borderRadius:6,
                          padding:"3px 10px", cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}
                      >
                        {copied === key ? "✓ Copied" : "Copy"}
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => onPickAccount && onPickAccount(acc)}
                    style={{
                      marginTop:6, width:"100%",
                      background:acc.color, color:"#fff",
                      border:"none", borderRadius:10,
                      padding:"11px 0", fontWeight:800, fontSize:14,
                      cursor:"pointer",
                    }}
                  >
                    Open {acc.role} Dashboard →
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <p style={{ textAlign:"center", fontSize:12, color:"#8aab98", margin:0 }}>
          Demo data resets daily. Your real data is safe.{" "}
          <a href="/" style={{ color:"#0a6e4e", fontWeight:700, textDecoration:"none" }}>
            Sign up free →
          </a>
        </p>
      </div>
    </div>
  );
}
